import { create } from 'zustand';
import { persist, type StorageValue } from 'zustand/middleware';

import type {
  ArmCombination,
  VariantArmId,
} from '~/features/configuration/types/treatment-configuration.types';
import { EXPERIMENT_POST_STEP_DEFAULTS } from '~/features/experiments/utils/content-attribute-steps';
import type { EnrichmentPostStepFlags } from '~/types/enrichment-flags';

import {
  DEFAULT_VISIBLE_COLUMNS,
  type ContentAttributeSelection,
  type CreateExperimentWizardState,
  type ExperimentChannel,
  type GroupingMethod,
  type GroupingMode,
  type ProductGroup,
  type Step2State,
  type TestDuration,
  type WizardStep,
} from '../types/create-experiment.types';

/* ------------------------------------------------------------------ */
/*  Initial states                                                    */
/* ------------------------------------------------------------------ */

const INITIAL_STEP1 = {
  method: null as GroupingMethod | null,
  manualRule: '',
  previewFetched: false,
};

/**
 * Default catalog-scoping filters applied on load (in-stock US English online).
 * Kept as a shared constant so callers (e.g. the AI-filter approve path) can
 * re-merge them and never drop the baseline scope. Locked on Define Scope: their
 * value can change but they can't be removed ("Reset to defaults" restores them).
 */
export const DEFAULT_SCOPE_FILTERS: Step2State['appliedFilters'] = {
  target_country: ['US'],
  availability: ['IN_STOCK'],
  content_language: ['en'],
  channel: ['online'],
};

/**
 * Layer a set of filters on top of the default catalog scope so the baseline
 * (in-stock/US/en/online) is never dropped. Incoming keys override the matching
 * default (e.g. the user asked for out-of-stock). Used by the AI-approve path.
 */
export function withDefaultScopeFilters(
  applied: Step2State['appliedFilters'],
): Step2State['appliedFilters'] {
  return { ...DEFAULT_SCOPE_FILTERS, ...applied };
}

const INITIAL_STEP2: Step2State = {
  search: '',
  page: 1,
  pageSize: 10,
  appliedFilters: { ...DEFAULT_SCOPE_FILTERS },
  visibleColumns: [...DEFAULT_VISIBLE_COLUMNS],
  uploadIdType: 'OFFER_ID',
  uploadedIdType: null,
  uploadCount: null,
  uploadPreviewIds: [],
  uploadFileName: null,
  // Default off — the uploaded candidate list is the scope; the operator opts
  // into the default catalog filters via the checkbox in the upload section.
};

const INITIAL_STEP3 = {
  mode: null as GroupingMode | null,
  group: null as ProductGroup | null,
  appliedFilters: {} as Step2State['appliedFilters'],
};

const INITIAL_STEP4 = {
  name: '',
  primaryKpi: null as string | null,
  secondaryKpi: null as string | null,
  armCombination: null as ArmCombination | null,
  // Per-arm treatment (enrichment schema) selections. Arm A is always Control
  // and derived from the catalog, so only the variant arms carry an id here.
  armBTreatmentId: null as string | null,
  armCTreatmentId: null as string | null,
  testDuration: null as TestDuration | null,
  // Per enriched arm: attribute id → sub-attribute id → selected. Empty until initialised by the screen.
  armContentAttributes: {} as Partial<Record<VariantArmId, ContentAttributeSelection>>,
  // Post-enrichment step flags for this experiment's runs. Not directly editable:
  // the descriptor flags are derived from the Conversational content attributes,
  // and scoring is pinned off (see `EXPERIMENT_POST_STEP_DEFAULTS`). Read at launch
  // by the review step, which is what puts them on the wire.
  postStepFlags: EXPERIMENT_POST_STEP_DEFAULTS,
  hasStoredArmAssignmentFile: false,
  retailer: '',
  targetLocales: [] as string[],
};

/* ------------------------------------------------------------------ */
/*  Actions                                                           */
/* ------------------------------------------------------------------ */

interface CreateExperimentActions {
  // Channel selection
  /** Persist the connection + connector code chosen on the channel picker. */
  setChannel: (channel: ExperimentChannel | null) => void;

  // Draft lifecycle
  /** Persist the draft experiment id returned by the server on step-1 completion. */
  setDraftId: (id: string | null) => void;
  /** Stashes the Define-Scope CSV until an experiment exists to attach it to. */
  setCandidateScopeFile: (file: File | null) => void;

  /**
   * Persist the catalog snapshot id captured from `/products/list`. Step 2
   * sets it on its first response; step 3 sets it defensively for deep-link /
   * resume paths. PATCHed onto the draft so downstream operations are pinned.
   */
  setSyncId: (syncId: string | null) => void;

  /**
   * Persist the step-2 scope SKU total (captured on step 3) so the review step
   * can derive the "All Other Products" count without re-querying the catalog.
   */
  setScopeSkuTotal: (total: number | null) => void;

  /**
   * Replace the entire wizard slice with the supplied partial — used by the
   * resume flow after fetching a draft. Replace (not merge) so state from a
   * different in-progress draft cannot bleed through.
   */
  hydrate: (partial: Partial<CreateExperimentWizardState>) => void;

  // Navigation
  setCurrentStep: (step: WizardStep) => void;
  markStepCompleted: (step: number) => void;
  goToNext: () => void;
  goToPrevious: () => void;

  // Step 1 — Define Product Groups
  setGroupingMethod: (method: GroupingMethod | null) => void;
  setManualRule: (rule: string) => void;
  setPreviewFetched: (fetched: boolean) => void;
  resetStep1: () => void;

  // Step 2 — Define Scope
  updateScope: (patch: Partial<Step2State>) => void;

  // Step 3 — Analytics Setup
  setGroupingMode: (mode: GroupingMode | null) => void;
  setGroup: (group: ProductGroup | null) => void;
  /** Persist the additive filter slice for step 3 — applied on top of step-2's locked filters. */
  setGroupingAppliedFilters: (applied: Step2State['appliedFilters']) => void;

  // Step 4 — Experiment Strategy
  setExperimentName: (name: string) => void;
  setPrimaryKpi: (kpi: string | null) => void;
  setSecondaryKpi: (kpi: string | null) => void;
  setArmCombination: (combination: ArmCombination | null) => void;
  /** Assign a treatment id to one variant arm (B or C). Arm A is locked to Control. */
  setArmTreatment: (armId: 'B' | 'C', treatmentId: string) => void;
  /** Swap the treatments assigned to Arm B and Arm C — the "Swap B & C" action. */
  swapArmTreatments: () => void;
  setTestDuration: (duration: TestDuration | null) => void;
  /** Replace every arm's content-attribute selection at once (init / hydration). */
  setArmContentAttributes: (
    byArm: Partial<Record<VariantArmId, ContentAttributeSelection>>,
  ) => void;
  setPostStepFlags: (flags: EnrichmentPostStepFlags) => void;
  /** google-agent retailer prompt slug for the experiment's runs; `''` = shared prompt. */
  setRetailer: (retailer: string) => void;
  /** Locales the experiment's runs also deliver, translated from English; `[]` = English only. */
  setTargetLocales: (targetLocales: string[]) => void;
  /** Records that the draft carries an arm-assignment CSV, so a resumed session
   *  can say so even though the `File` itself is long gone. */
  setStoredArmAssignmentFile: (has: boolean) => void;
  /** Toggle one sub-attribute under one content attribute section of one arm. */
  toggleContentSubAttribute: (armId: VariantArmId, attrId: string, subId: string) => void;
  /**
   * Toggle all sub-attributes within a section of one arm.
   * If any sub is selected → deselect all; if none → select all.
   */
  toggleContentAttributeAll: (armId: VariantArmId, attrId: string) => void;

  // Reset
  reset: () => void;
}

/* ------------------------------------------------------------------ */
/*  Store                                                             */
/* ------------------------------------------------------------------ */

const INITIAL_STATE: CreateExperimentWizardState = {
  channel: null,
  draftId: null,
  candidateScopeFile: null,
  syncId: null,
  scopeSkuTotal: null,
  currentStep: 1,
  completedSteps: [],
  step1: INITIAL_STEP1,
  step2: INITIAL_STEP2,
  step3: INITIAL_STEP3,
  step4: INITIAL_STEP4,
};

export const useCreateExperimentStore = create<
  CreateExperimentWizardState & CreateExperimentActions
>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,

      // -- Channel selection ----------------------------------------

      setChannel: (channel) => {
        set({ channel });
      },

      // -- Draft lifecycle ------------------------------------------

      setDraftId: (id) => {
        set({ draftId: id });
      },

      setCandidateScopeFile: (file) => {
        set({ candidateScopeFile: file });
      },

      setSyncId: (syncId) => {
        // Idempotent — avoid an unnecessary state update if the value matches.
        set((s) => (s.syncId === syncId ? s : { syncId }));
      },

      setScopeSkuTotal: (total) => {
        // Idempotent — skip the write when the count is unchanged.
        set((s) => (s.scopeSkuTotal === total ? s : { scopeSkuTotal: total }));
      },

      // -- Hydrate (resume) -----------------------------------------

      hydrate: (partial) => {
        // Replace, not merge — a stale in-progress draft for a different
        // experiment must not bleed into the resumed state.
        set({ ...INITIAL_STATE, ...partial });
      },

      // -- Navigation ------------------------------------------------

      setCurrentStep: (step) => set({ currentStep: step }),

      markStepCompleted: (step) =>
        set((s) => ({
          completedSteps: s.completedSteps.includes(step)
            ? s.completedSteps
            : [...s.completedSteps, step],
        })),

      goToNext: () =>
        set((s) => {
          const next = Math.min(s.currentStep + 1, 5) as WizardStep;
          return {
            currentStep: next,
            completedSteps: s.completedSteps.includes(s.currentStep)
              ? s.completedSteps
              : [...s.completedSteps, s.currentStep],
          };
        }),

      goToPrevious: () =>
        set((s) => ({
          currentStep: Math.max(s.currentStep - 1, 1) as WizardStep,
        })),

      // -- Step 1 ----------------------------------------------------

      setGroupingMethod: (method) => set((s) => ({ step1: { ...s.step1, method } })),

      setManualRule: (rule) => set((s) => ({ step1: { ...s.step1, manualRule: rule } })),

      setPreviewFetched: (fetched) =>
        set((s) => ({ step1: { ...s.step1, previewFetched: fetched } })),

      resetStep1: () => set({ step1: INITIAL_STEP1 }),

      // -- Step 2 ----------------------------------------------------

      updateScope: (patch) => set((s) => ({ step2: { ...s.step2, ...patch } })),

      // -- Step 3 ----------------------------------------------------

      setGroupingMode: (mode) => set((s) => ({ step3: { ...s.step3, mode } })),

      setGroup: (group) => set((s) => ({ step3: { ...s.step3, group } })),

      setGroupingAppliedFilters: (applied) =>
        set((s) => ({ step3: { ...s.step3, appliedFilters: applied } })),

      // -- Step 4 ----------------------------------------------------

      setExperimentName: (name) => set((s) => ({ step4: { ...s.step4, name } })),

      setPrimaryKpi: (kpi) => set((s) => ({ step4: { ...s.step4, primaryKpi: kpi } })),

      setSecondaryKpi: (kpi) => set((s) => ({ step4: { ...s.step4, secondaryKpi: kpi } })),

      setArmCombination: (combination) =>
        set((s) => ({ step4: { ...s.step4, armCombination: combination } })),

      setArmTreatment: (armId, treatmentId) =>
        set((s) => ({
          step4: {
            ...s.step4,
            ...(armId === 'B'
              ? { armBTreatmentId: treatmentId }
              : { armCTreatmentId: treatmentId }),
          },
        })),

      swapArmTreatments: () =>
        set((s) => ({
          step4: {
            ...s.step4,
            armBTreatmentId: s.step4.armCTreatmentId,
            armCTreatmentId: s.step4.armBTreatmentId,
          },
        })),

      setTestDuration: (duration) =>
        set((s) => ({ step4: { ...s.step4, testDuration: duration } })),

      setPostStepFlags: (flags) => set((s) => ({ step4: { ...s.step4, postStepFlags: flags } })),
      setRetailer: (retailer) => set((s) => ({ step4: { ...s.step4, retailer } })),
      setTargetLocales: (targetLocales) => set((s) => ({ step4: { ...s.step4, targetLocales } })),

      setStoredArmAssignmentFile: (has) =>
        set((s) => ({ step4: { ...s.step4, hasStoredArmAssignmentFile: has } })),
      setArmContentAttributes: (byArm) =>
        set((s) => ({ step4: { ...s.step4, armContentAttributes: byArm } })),

      toggleContentSubAttribute: (armId, attrId, subId) =>
        set((s) => {
          const armSelection = s.step4.armContentAttributes[armId] ?? {};
          const existingSection = armSelection[attrId] ?? {};
          return {
            step4: {
              ...s.step4,
              armContentAttributes: {
                ...s.step4.armContentAttributes,
                [armId]: {
                  ...armSelection,
                  [attrId]: { ...existingSection, [subId]: !existingSection[subId] },
                },
              },
            },
          };
        }),

      toggleContentAttributeAll: (armId, attrId) =>
        set((s) => {
          const armSelection = s.step4.armContentAttributes[armId] ?? {};
          const section = armSelection[attrId] ?? {};
          const anySelected = Object.values(section).some(Boolean);
          // Flip every sub-id to the target state (false if any were selected, true otherwise).
          const next: Record<string, boolean> = {};
          for (const id of Object.keys(section)) {
            next[id] = !anySelected;
          }
          return {
            step4: {
              ...s.step4,
              armContentAttributes: {
                ...s.step4.armContentAttributes,
                [armId]: { ...armSelection, [attrId]: next },
              },
            },
          };
        }),

      // -- Reset -----------------------------------------------------

      reset: () => set(INITIAL_STATE),
    }),
    {
      name: 'create-experiment-store',
      // v13 — added top-level `scopeSkuTotal: number | null` captured on step 3
      // so the review step can show the "All Other Products" segment count
      // (scope total − group SKUs) without an extra product-list request.
      // v12 — added content_language=en and channel=online to the default
      // step2 applied filters alongside the existing target_country / availability seeds.
      // v11 — seeded `step2.appliedFilters` with default target_country=US and
      // availability=IN_STOCK so the product listing opens pre-filtered.
      // v10 — added top-level `syncId: string | null` captured from
      // `/products/list` and PATCHed onto the draft so downstream flows
      // (scoring / enrichment / pipeline) read the same catalog snapshot.
      // v9 — replaced `connectorCode: string | null` with
      // `channel: { connectionId, code } | null` so the connection identity
      // travels with the code (unblocks the resume flow's `POST /experiments`
      // payload + GET-response hydration).
      // v8 — `ProductGroup` gained `filters: AppliedProductFilters` so the
      // group can carry the snapshot sent to `groupingFilter.filters` on the
      // PATCH. v7 state's group lacks the field — reset.
      // v7 — added `step3.appliedFilters` for step-3's additive filter slice.
      // v6 — `step2.filters: CatalogFilters` replaced by `step2.appliedFilters`.
      // v5 — added `connectorCode` and switched `step1.method` to uppercase
      // backend codes. v4 reshaped `contentAttributes`.
      // v23 — `step4.contentAttributes` replaced by `step4.armContentAttributes`
      // (one selection per enriched arm, B / C).
      // v22 — added `step4.hasStoredArmAssignmentFile` so a resumed draft can
      // say an arm-assignment CSV is already attached; the File itself is
      // in-memory only and doesn't survive a reload.
      // v21 — added `step2.uploadPreviewIds`. integration-service now has
      // `offer_id` / `item_group_id` / `external_id` list filters, so the preview
      // can narrow again; the array is capped at the endpoint's 4,000-value
      // limit and is a display concern only.
      // v20 — added `step2.uploadedIdType`: the identifier the STORED scope
      // holds, separate from the toggle. Switching the toggle no longer clears
      // an existing scope, so the two can differ until the next upload.
      // v19 — `step2.uploadIds` (the parsed id array) replaced by
      // `uploadCount` + `uploadIdType`. The product list can't be filtered by
      // either identifier today, so holding the array only meant shipping it in
      // request bodies for nothing; the uploaded file is the scope.
      // v18 — dropped `step2.uploadKeepDefaults`. The uploaded list is now an
      // option inside Manual scope and always ANDs with the catalog filters
      // (a product must satisfy both), so a toggle that could switch the
      // filters off no longer has a meaning.
      // v17 — added `step2.uploadIds` / `uploadFileName` / `uploadKeepDefaults`
      // for the "Upload list" scope mode (candidate-file product_id scoping).
      // v16 — added `step4.armBTreatmentId` / `step4.armCTreatmentId` for the
      // per-arm treatment (enrichment schema) selection on the Strategy step.
      // v15 — `step4.postStepFlags` seeds scoring to explicit `false`; a v14 blob
      // holds `{}`, which would let the server default decide instead.
      // v14 — added `step4.postStepFlags`.
      version: 23,
      migrate: () => INITIAL_STATE,
      // `candidateScopeFile` holds a `File`, which JSON.stringify flattens to
      // `{}` — persisting it would restore a broken object that looks present
      // but can't be uploaded. Excluded, so a reload drops the pending file and
      // the UI falls back to the ids-only summary (which does persist).
      // Persist everything except the pending `File`, which JSON.stringify
      // flattens to `{}` — restoring that would look like a file is attached
      // when it can no longer be uploaded. Nulled instead, so a reload falls
      // back to the ids-only summary (which does persist).
      partialize: (state) => ({ ...state, candidateScopeFile: null }),
      // Custom sessionStorage adapter (wizard state shouldn't survive tab close).
      storage: {
        getItem: (name) => {
          const value = sessionStorage.getItem(name);
          return value ? (JSON.parse(value) as StorageValue<CreateExperimentWizardState>) : null;
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        },
      },
    },
  ),
);
